import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/constants';
import { useAuth } from '../context/AuthContext';

// Formatiranje vremena (npr. "danas u 10:45", "jučer u 16:30")
const formatRelativeTime = (dateInput) => {
  if (!dateInput) return '';
  const now = new Date();
  const date = new Date(dateInput);
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  const timeStr = date.toLocaleTimeString('hr-HR', { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 0) return `danas u ${timeStr}`;
  if (diffDays === 1) return `jučer u ${timeStr}`;
  if (diffDays < 7) return `prije ${diffDays} dana`;
  return date.toLocaleDateString('hr-HR');
};

const ITEMS_PER_PAGE = 15; // Broj aktivnosti po stranici

export default function Activities() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const { userInfo } = useAuth();
  const companyID = userInfo?.companyID || 1;

  // ESC tipka briše unos u tražilici
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        if (searchTerm) {
          setSearchTerm('');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [searchTerm]);

  // Vraćanje na prvu stranicu kod filtriranja ili pretraživanja
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory]);

  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem('token');

    fetch(`${API_BASE_URL}/activityList?companyID=${companyID}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
      .then(res => (res.ok ? res.text() : ''))
      .then(text => {
        try {
          const data = text ? JSON.parse(text) : [];
          const sorted = Array.isArray(data) ? data.sort((a, b) => (b.activityID || b.id) - (a.activityID || a.id)) : [];
          setActivities(sorted);
        } catch (e) {
          setActivities([]);
        }
      })
      .catch(err => {
        console.error("Greška aktivnosti:", err);
      })
      .finally(() => setLoading(false));
  }, [companyID]);

  // Određivanje ikone i boje prema vrsti radnje
  const getActionStyle = (type = '', body = '') => {
    const t = type.toLowerCase();
    const b = body.toLowerCase();

    // NOVO: Prepoznavanje troškova i isplata
    if (t.includes('expense') || b.includes('trošak') || b.includes('trosak') || b.includes('isplat')) {
      return {
        category: 'expenses',
        icon: 'payments',
        iconBg: 'bg-emerald-50 text-emerald-600 border border-emerald-200/60'
      };
    }
    if (t.includes('meeting') || b.includes('sastanak')) {
      return {
        category: 'meeting',
        icon: 'event',
        iconBg: 'bg-sky-50 text-sky-600 border border-sky-200/60'
      };
    }
    if (t.includes('document') || b.includes('dokument') || b.includes('predugovor')) {
      return {
        category: 'document',
        icon: 'description',
        iconBg: 'bg-teal-50 text-teal-600 border border-teal-200/60'
      };
    }
    if (t.includes('customer') || b.includes('kup') || b.includes('klijent')) {
      return {
        category: 'crm',
        icon: 'person',
        iconBg: 'bg-indigo-50 text-indigo-600 border border-indigo-200/60'
      };
    }
    if (t.includes('unit') || b.includes('stan')) {
      return {
        category: 'units',
        icon: 'apartment',
        iconBg: 'bg-violet-50 text-violet-600 border border-violet-200/60'
      };
    }
    if (t.includes('work_order') || b.includes('nalog')) {
      return {
        category: 'work_orders',
        icon: 'assignment',
        iconBg: 'bg-amber-50 text-amber-600 border border-amber-200/60'
      };
    }
    return {
      category: 'other',
      icon: 'history',
      iconBg: 'bg-slate-100 text-slate-600 border border-slate-200'
    };
  };

  // Filtriranje
  const filteredActivities = activities.filter(act => {
    const text = (act.body || '').toLowerCase();
    const style = getActionStyle(act.type, act.body);
    const matchesSearch = text.includes(searchTerm.toLowerCase());
    
    if (selectedCategory === 'all') return matchesSearch;
    return matchesSearch && style.category === selectedCategory;
  });

  // Paginacija izračuni
  const totalItems = filteredActivities.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE) || 1;
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalItems);
  const currentActivities = filteredActivities.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Razdvaja ime korisnika od opisa radnje
  const renderFormattedBody = (body = '') => {
    const parts = body.split(' - ');
    if (parts.length >= 2) {
      const user = parts[0];
      const rest = parts.slice(1).join(' - ');
      return (
        <p className="text-xs text-slate-700 leading-snug">
          <strong className="font-bold text-slate-900">{user}</strong>
          <span className="text-slate-400 font-normal mx-1.5">—</span>
          <span>{rest}</span>
        </p>
      );
    }
    return <p className="text-xs text-slate-800 font-medium leading-snug">{body}</p>;
  };

  // Popis kategorija s dodanim "Troškovi"
  const categories = [
    { id: 'all', label: 'Sve' },
    { id: 'crm', label: 'Kupci' },
    { id: 'expenses', label: 'Troškovi' }, // NOVO
    { id: 'meeting', label: 'Sastanci' },
    { id: 'document', label: 'Dokumenti' },
    { id: 'units', label: 'Stanovi' },
    { id: 'work_orders', label: 'Nalozi' }
  ];

  return (
    <div className="max-w-6xl space-y-6 pb-12">
      
      {/* 1. NASLOV STRANICE */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-extrabold text-slate-900 tracking-tight">Aktivnosti</h1>
          <p className="text-xs text-slate-400 font-medium mt-0.5">Pregled zadnjih radnji i izmjena unutar tima</p>
        </div>
        
        <span className="text-xs font-bold bg-white border border-slate-200 px-3 py-1.5 rounded-xl text-slate-600 shadow-2xs">
          Ukupno: <strong className="text-slate-900">{totalItems}</strong>
        </span>
      </div>

      {/* 2. PRETRAŽIVAČ I FILTERI */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row gap-3 items-center justify-between">
        
        {/* Pretraga s ikonom */}
        <div className="relative w-full md:w-80">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">
            search
          </span>
          <input 
            type="text" 
            placeholder="Pretraži aktivnosti..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3.5 py-2 text-xs font-semibold text-slate-800 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:border-slate-400 transition" 
          />
        </div>

        {/* Filteri */}
        <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto pb-1 md:pb-0">
          {categories.map(cat => {
            const isActive = selectedCategory === cat.id;
            return (
              <button 
                key={cat.id} 
                type="button"
                onClick={() => setSelectedCategory(cat.id)} 
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer shrink-0 ${
                  isActive 
                    ? 'bg-slate-900 text-white shadow-xs' 
                    : 'bg-slate-50 hover:bg-slate-100 text-slate-600 border border-slate-200/70'
                }`}
              >
                {cat.label}
              </button>
            );
          })}
        </div>

      </div>

      {/* 3. POPIS AKTIVNOSTI S PAGINACIJOM */}
      {loading ? (
        <p className="text-slate-400 text-center py-16 text-xs font-semibold">Učitavanje aktivnosti...</p>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
          
          <div className="divide-y divide-slate-100">
            {currentActivities.length === 0 ? (
              <div className="py-16 text-center text-xs text-slate-400 font-medium">
                Nema pronađenih aktivnosti za odabrane kriterije.
              </div>
            ) : (
              currentActivities.map(act => {
                const style = getActionStyle(act.type, act.body);
                const actDate = act.createdAT || act.createdAt || act.createdat;

                return (
                  <div 
                    key={act.activityID || act.id} 
                    className="p-4 hover:bg-slate-50/70 transition flex items-center justify-between gap-4"
                  >
                    {/* Lijeva strana: Ikona u boji + Formatirani tekst */}
                    <div className="flex items-center gap-3.5 min-w-0">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-2xs ${style.iconBg}`}>
                        <span className="material-symbols-outlined text-lg">{style.icon}</span>
                      </div>

                      <div className="min-w-0">
                        {renderFormattedBody(act.body)}
                      </div>
                    </div>

                    {/* Desna strana: Točno vrijeme */}
                    <span className="text-[11px] text-slate-400 font-semibold shrink-0 text-right">
                      {formatRelativeTime(actDate)}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {/* 4. TRAKA ZA PAGINACIJU (PRETHODNA / SLJEDEĆA) */}
          {totalItems > 0 && (
            <div className="p-4 bg-slate-50/60 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
              
              {/* Raspon prikazanih zapisa */}
              <div className="text-slate-500 font-medium">
                Prikazano <span className="font-bold text-slate-900">{startIndex + 1}–{endIndex}</span> od{' '}
                <span className="font-bold text-slate-900">{totalItems}</span> aktivnosti
              </div>

              {/* Gumbi za navigaciju */}
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white font-bold text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-2xs cursor-pointer flex items-center gap-1"
                >
                  <span className="text-sm leading-none">‹</span> Prethodna
                </button>

                <div className="px-3 py-1.5 rounded-xl bg-white border border-slate-200 font-bold text-slate-800 shadow-2xs">
                  {currentPage} / {totalPages}
                </div>

                <button
                  type="button"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white font-bold text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-2xs cursor-pointer flex items-center gap-1"
                >
                  Sljedeća <span className="text-sm leading-none">›</span>
                </button>
              </div>

            </div>
          )}

        </div>
      )}

    </div>
  );
}